import { toast } from 'react-toastify';
import { Checkbox } from '@mui/material';
import { useHistory } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import { Visibility as ViewIcon } from '@mui/icons-material';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import ViewStateManager from '../../utils/viewStateManager';
import TagsManager from '../../components/Tags/TagsManager';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { setActiveActivityBox } from '../../store/Activities';
import { updateObject, createObject } from '../../store/Objects';
import PlanAnimation from '../../components/Animations/PlanAnimation.js';
import { getPipelines, getPipelineBoard, moveDealStage, setPipelineBoard, setLoading } from '../../store/Deals';

const PipelineView = ({ selectItems, setSelectItems, setIsSideViewModal }) => {

  const history = useHistory();
  const dispatch = useDispatch();
  const { pipelineBoard, loading } = useSelector(state => state['Deals']);
  const { currentWorkspace } = useSelector(state => state['Settings']);

  const [pipeline, setPipeline] = useState([]);
  const [highlightedStage, setHighlightedStage] = useState(null);
  const [isVisibleBlocks, setIsVisibleBlocks] = useState(() => ViewStateManager.getPipelineCollapsedStages());

  const quickAddInputRef = useRef(null);
  const [quickAddStage, setQuickAddStage] = useState(null); // { stageId, title }
  const [quickAddOwnerPos, setQuickAddOwnerPos] = useState({ top: 0, left: 0 });

  const editInputRef = useRef(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [ownerPickerDealId, setOwnerPickerDealId] = useState(null);
  const [tagPickerDealId, setTagPickerDealId] = useState(null);
  const [tagPickerPos, setTagPickerPos] = useState({ top: 0, left: 0 });
  const members = currentWorkspace?.['members'] || [];

  const handleStartRename = (e, deal) => {
    e.stopPropagation();
    setEditingDeal({ id: deal['_id'], title: deal['title'] });
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const handleSaveRename = () => {
    if (!editingDeal) return;
    const trimmed = editingDeal['title'].trim();
    if (!trimmed) return setEditingDeal(null);
    dispatch(updateObject({ objectType: 'deals', id: editingDeal['id'], data: { title: trimmed }, successCallback: () => setPipeline(prev => ({ ...prev, stages: prev['stages'].map(s => ({ ...s, deals: s['deals'].map(d => d['_id'] === editingDeal['id'] ? { ...d, title: trimmed } : d) })) })) }));
    setEditingDeal(null);
  };

  const openQuickAdd = (stageId) => {
    setQuickAddStage({ stageId, title: '' });
    setTimeout(() => quickAddInputRef.current?.focus(), 0);
  };

  const handleQuickAddSave = () => {
    const title = quickAddStage?.['title']?.trim();
    if (!title) return setQuickAddStage(null);
    const stageId = quickAddStage['stageId'];
    const data = { title, stage: stageId, pipelineId: pipeline['_id'], tags: quickAddStage['tags'] || [] };
    if (quickAddStage['owner']?._id) data['ownerId'] = quickAddStage['owner']['_id'];
    setQuickAddStage(prev => ({ ...prev, saving: true }));
    dispatch(createObject({
      objectType: 'deals',
      data,
      successCallback: () => {
        setQuickAddStage(null);
        dispatch(setLoading(true));
        dispatch(getPipelineBoard());
      },
      failCallback: () => setQuickAddStage(prev => ({ ...prev, saving: false })),
    }));
  };

  const handleCopyLink = (e, dealId) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}${getWorkspacePath(`/deals/${dealId}`, currentWorkspace?.['_id'])}`).then(() => toast.success('Deal link copied to clipboard', { autoClose: 2000 }));
  };

  useEffect(() => {
    if (!ownerPickerDealId) return;
    const handleClose = (e) => { if (!e.target.closest('.owner-picker-dropdown')) setOwnerPickerDealId(null); };
    document.addEventListener('mousedown', handleClose);
    return () => document.removeEventListener('mousedown', handleClose);
  }, [ownerPickerDealId]);

  useEffect(() => {
    if (!tagPickerDealId) return;
    const handleClose = (e) => { if (!e.target.closest('.card-tag-picker-dropdown')) setTagPickerDealId(null); };
    document.addEventListener('mousedown', handleClose);
    return () => document.removeEventListener('mousedown', handleClose);
  }, [tagPickerDealId]);

  useEffect(() => { dispatch(getPipelines()) }, []);
  useEffect(() => {
    setPipeline(pipelineBoard);
    if (pipelineBoard?.stages) dispatch(setLoading(false));
  }, [pipelineBoard]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectItems.length > 0) setSelectItems([]);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectItems]);

  const onDragStart = useCallback((evt) => {
    let element = evt['currentTarget'];
    element['classList'].add('dragged');
    evt['dataTransfer'].setData('text/plain', element['id']);
    evt['dataTransfer']['effectAllowed'] = 'move';

    // Add global dragging class for cursor
    document.body.classList.add('dragging');
  }, []);

  const onDragEnter = useCallback((evt) => {
    evt.preventDefault();
    evt['currentTarget']['classList'].add('dragged-over');
    evt['dataTransfer']['dropEffect'] = 'move';

    // Add drag-active class only to this column's drop container
    const dropContainer = evt.currentTarget.querySelector('.drag_row');
    if (dropContainer) {
      dropContainer.classList.add('drag-active');
    }
  }, []);

  const onDragLeave = useCallback((evt) => {
    let newTarget = evt['relatedTarget'];
    let currentTarget = evt['currentTarget'];
    if (newTarget?.['parentNode'] === currentTarget || newTarget === currentTarget) return;
    evt.preventDefault();
    evt['currentTarget']['classList'].remove('dragged-over');

    // Remove drag-active and clear any highlights when leaving the column
    const dropContainer = evt.currentTarget.querySelector('.drag_row');
    if (dropContainer) {
      dropContainer.classList.remove('drag-active');
      const highlights = dropContainer.querySelectorAll('.drop-zone.highlight');
      highlights.forEach(zone => zone.classList.remove('highlight'));
    }
  }, []);

  const onDragOver = useCallback((evt) => {
    evt.preventDefault();
    evt['dataTransfer']['dropEffect'] = 'move';

    // Highlight drop zones based on mouse position
    const dropContainer = evt.currentTarget.querySelector('.drag_row');
    if (dropContainer) {
      const rect = dropContainer.getBoundingClientRect();
      const dropY = evt.clientY - rect.top;

      // Remove all existing highlights
      const existingHighlights = dropContainer.querySelectorAll('.drop-zone.highlight');
      existingHighlights.forEach(zone => zone.classList.remove('highlight'));

      // Find which drop zone to highlight
      const dropZones = Array.from(dropContainer.querySelectorAll('.drop-zone'));
      let closestZone = null;
      let minDistance = Infinity;

      dropZones.forEach(zone => {
        const zoneRect = zone.getBoundingClientRect();
        const zoneY = zoneRect.top + zoneRect.height / 2 - rect.top;
        const distance = Math.abs(dropY - zoneY);

        if (distance < minDistance) {
          minDistance = distance;
          closestZone = zone;
        }
      });

      // Highlight the closest drop zone
      if (closestZone && minDistance < 50) return closestZone.classList.add('highlight'); // Within 50px threshold
    }
  }, []);

  const toggleVisibilityBlocks = (stage) => {
    setIsVisibleBlocks((prev) => {
      const newState = { ...prev, [stage]: !prev[stage] };
      ViewStateManager.setPipelineCollapsedStages(newState);
      return newState;
    });
  };

  const handleSelectCheckBox = (dealId) => {
    if (selectItems.includes(dealId)) setSelectItems(selectItems.filter((id) => id !== dealId));
    else setSelectItems([...selectItems, dealId]);
  };

  // Shared drop handler
  const handleDropLogic = (evt, newStage) => {
    evt.preventDefault?.();
    evt['currentTarget']?.['classList']?.remove('dragged-over');

    const data = evt['dataTransfer']?.getData('text/plain');
    if (!data) return;

    let dealData, prevStage;
    for (let stage of pipeline['stages']) {
      let dealFound = stage['deals'].find((deal) => deal['_id'] == data);
      if (dealFound) {
        dealData = dealFound;
        prevStage = stage['stageId'];
      }
    }
    if (!dealData) return;

    // --- Calculate drop position ---
    const targetStage = pipeline['stages'].find(s => s['stageId'] === newStage);
    let targetPosition = 0;

    const dropContainer = evt.currentTarget?.querySelector?.('.drag_row');
    if (dropContainer) {
      const highlightedZone = dropContainer.querySelector('.drop-zone.highlight');
      if (highlightedZone) {
        const position = parseInt(highlightedZone.getAttribute('data-position'));
        targetPosition = isNaN(position) ? 0 : position;
      } else if (targetStage && targetStage['deals'].length > 0) {
        const rect = dropContainer.getBoundingClientRect();
        const dropY = evt.clientY - rect.top;

        const dealElements = Array.from(dropContainer.querySelectorAll('.box-card'));

        for (let i = 0; i < dealElements.length; i++) {
          const dealRect = dealElements[i].getBoundingClientRect();
          const dealMiddle = dealRect.top + dealRect.height / 2 - rect.top;

          if (dropY < dealMiddle) {
            targetPosition = i;
            break;
          }
          targetPosition = i + 1;
        }
      }
    }

    // --- Cleanup visual feedback ---
    const allContainers = document.querySelectorAll('.drag_row');
    allContainers.forEach(container => {
      container.classList.remove('drag-active');
      const highlights = container.querySelectorAll('.drop-zone.highlight');
      highlights.forEach(zone => zone.classList.remove('highlight'));
    });
    document.body.classList.remove('dragging');

    // --- Update pipeline ---
    let targetStageDeals = [];
    for (let stage of pipeline['stages']) {
      if (stage['stageId'] === newStage) {
        stage['deals'].splice(targetPosition, 0, dealData);
        targetStageDeals = stage['deals'];
      }
      if (stage['stageId'] === prevStage) stage['deals'] = stage['deals'].filter((deal) => deal['_id'] !== data);
    }

    const dealPositions = targetStageDeals.map((deal, index) => ({ dealId: deal['_id'], position: index }));
    dispatch(moveDealStage({ id: data, stage: newStage, targetPosition, dealPositions }));
    setPipeline({ ...pipeline, stages: pipeline['stages'] });
  }

  // --- Reuse in both events ---
  const onDrop = useCallback((evt, _, newStage) => {
    handleDropLogic(evt, newStage);
  }, [pipeline]);

  const onDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;

    // ❌ agar koi destination nahi hai
    if (!destination) return;

    // ❌ agar wahi column + wahi index hai
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // pipeline ko clone karna zaroori hai (immutability)
    const newPipeline = { ...pipeline, stages: pipeline.stages.map(stage => ({ ...stage, deals: [...stage.deals], })), };

    // source aur destination stage find karo
    const sourceStage = newPipeline.stages.find(s => s.stageId === source.droppableId);
    const destStage = newPipeline.stages.find(s => s.stageId === destination.droppableId);

    if (!sourceStage || !destStage) return;

    // 🟢 dragged deal find
    const draggedDeal = sourceStage.deals.find(d => d._id === draggableId);
    if (!draggedDeal) return;

    // Check if destination stage is collapsed (closed)
    const isDestStageCollapsed = isVisibleBlocks[destStage.stageId];

    // 1) remove from source
    sourceStage.deals = sourceStage.deals.filter(d => d._id !== draggableId);

    // 2) insert into destination
    destStage.deals.splice(destination.index, 0, draggedDeal);

    // backend ko bhejne ke liye naya order
    const dealPositions = destStage.deals.map((deal, index) => ({ dealId: deal._id, position: index, }));

    // Update local state immediately for responsive UI
    setPipeline(newPipeline);

    // Also update Redux state immediately so it persists across navigation
    dispatch(setPipelineBoard({ ...pipelineBoard, stages: newPipeline.stages }));

    // Show notification if deal was moved to a collapsed column
    if (isDestStageCollapsed) {
      const dealTitle = draggedDeal.title || draggedDeal.name || 'Deal';
      const stageName = destStage.stageName || destStage.name || 'Stage';

      // Show toast notification
      toast.success(`"${dealTitle}" moved to ${stageName}`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, });

      // Temporarily highlight the collapsed column
      setHighlightedStage(destStage['stageId']);
      setTimeout(() => { setHighlightedStage(null); }, 2000); // Remove highlight after 2 seconds
    }

    // redux dispatch - saga will make API call to save to backend
    dispatch(moveDealStage({ id: draggableId, stage: destStage['stageId'], targetPosition: destination.index, dealPositions, }));
  }, [pipeline, pipelineBoard, dispatch, isVisibleBlocks]);

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className='row'>
          {loading ? <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'calc(100vh - 165px)' }}><PlanAnimation /></div> : pipeline && pipeline['stages'] ? pipeline['stages'].map((stage, idx) => (
            <div className={`${isVisibleBlocks[stage['stageId']] ? 'col-2 active' : 'col-2'}`} key={stage.stageId}>
              <Droppable droppableId={stage.stageId} type="DEAL">
                {(provided) => (
                  <div
                    className={`${stage['stageId'].toLowerCase().replace(/\s/g, '-')}-box small-box`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <section className='drag_container'>
                      {isVisibleBlocks[stage['stageId']]
                        ? <div className={`container vertical-button-sec ${highlightedStage === stage['stageId'] ? 'highlighted-column' : ''}`}>
                          <div className='drag_column vertical-mode'>
                            <div className='title-area vertical'>
                              <button className='arrow-btn' onClick={() => toggleVisibilityBlocks(stage['stageId'])}><i className='icon'><svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='0.5' y='0.5' width='14' height='14' rx='7' fill='#FBFBFF' stroke='#4B5563' /><path d='M8.52284 10.06L6 7.53003L8.52284 5' stroke='#4B5563' stroke-linecap='round' stroke-linejoin='round' /></svg></i></button>
                              <h4>{stage['stageName']} <span>{stage['deals'].length}</span></h4>
                            </div>
                            <div className='bottom-area'>
                              <h4>
                                <span>Total:</span> {
                                  stage['deals'].reduce((acc, deal) => {
                                    let num = 0;
                                    if (deal['amount']) num = parseFloat(deal['amount'] || 0);
                                    return acc + num;
                                  }, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
                                }
                              </h4>
                            </div>
                          </div>
                        </div>
                        : <div className='container'>
                          <div className='drag_column'>
                            <div className='title-area'>
                              <div className='stage-title-group'>
                                <h4>{stage['stageName']}</h4>
                                <button className='stage-quick-add-btn' onClick={(e) => { e.stopPropagation(); openQuickAdd(stage['stageId']); }}><i className='icon'><svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='0.5' y='0.5' width='14' height='14' rx='7' fill='#FBFBFF' stroke='#4B5563' /><path d='M7.5 4.5V10.5M4.5 7.5H10.5' stroke='#4B5563' stroke-linecap='round' stroke-linejoin='round' /></svg></i><div className='overlaybox-tooltip'><p>Quick Add</p></div></button>
                              </div>
                              <div className='right-btn-area-arrow'>
                                <span>{stage['deals'].length}</span>
                                <button className='arrow-btn' onClick={() => toggleVisibilityBlocks(stage['stageId'])}><i className='icon'><svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='0.5' y='0.5' width='14' height='14' rx='7' fill='#FBFBFF' stroke='#4B5563' /><path d='M8.52284 10.06L6 7.53003L8.52284 5' stroke='#4B5563' stroke-linecap='round' stroke-linejoin='round' /></svg></i></button>
                              </div>
                            </div>
                            <div
                              className='drag_row'
                            >
                              {quickAddStage?.['stageId'] === stage['stageId'] && (
                                <div className='quick-add-card'>
                                  <div className='quick-add-card__name-row'>
                                    <input
                                      ref={quickAddInputRef}
                                      className='quick-add-card__input'
                                      placeholder='Deal name...'
                                      value={quickAddStage['title']}
                                      onChange={(e) => setQuickAddStage(prev => ({ ...prev, title: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAddSave(); if (e.key === 'Escape') setQuickAddStage(null); }}
                                    />
                                  </div>
                                  <div className='quick-add-card__row' onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setQuickAddOwnerPos({ top: r.bottom + 4, left: r.left }); setQuickAddStage(prev => ({ ...prev, showOwnerPicker: !prev['showOwnerPicker'], showTagPicker: false })); }}>
                                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M7 7.875C8.933 7.875 10.5 6.308 10.5 4.375C10.5 2.442 8.933 0.875 7 0.875C5.067 0.875 3.5 2.442 3.5 4.375C3.5 6.308 5.067 7.875 7 7.875Z' stroke='#8C8C8C' strokeWidth='0.8' /><path d='M12.025 13.125C12.025 10.7625 9.7738 8.8375 7.0003 8.8375C4.2268 8.8375 1.97559 10.7625 1.97559 13.125' stroke='#8C8C8C' strokeWidth='0.8' /></svg>
                                    <span className={`quick-add-card__row-label${quickAddStage['owner'] ? ' quick-add-card__row-label--selected' : ''}`}>
                                      {quickAddStage['owner'] ? `${quickAddStage['owner']['firstName']} ${quickAddStage['owner']['lastName']}` : 'Add owner'}
                                    </span>
                                  </div>
                                  <div className='quick-add-card__row' onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setTagPickerPos({ top: r.bottom + 4, left: r.left }); setQuickAddStage(prev => ({ ...prev, showTagPicker: !prev['showTagPicker'], showOwnerPicker: false })); }}>
                                    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M1.33 2.67V6.78C1.33 7.14 1.47 7.49 1.72 7.74L7.59 13.61C8.11 14.13 8.96 14.13 9.48 13.61L13.61 9.48C14.13 8.96 14.13 8.11 13.61 7.59L7.74 1.72C7.49 1.47 7.14 1.33 6.78 1.33H2.67C1.93 1.33 1.33 1.93 1.33 2.67Z' stroke='#8C8C8C' strokeWidth='0.8' strokeLinecap='round' strokeLinejoin='round' /><path d='M4.67 5.67C5.22 5.67 5.67 5.22 5.67 4.67C5.67 4.12 5.22 3.67 4.67 3.67C4.12 3.67 3.67 4.12 3.67 4.67C3.67 5.22 4.12 5.67 4.67 5.67Z' stroke='#8C8C8C' strokeWidth='0.8' /></svg>
                                    {quickAddStage['tags']?.filter(t => t != null).length > 0
                                      ? <div className='card-tags-inline' style={{ marginLeft: 0 }}>
                                        {quickAddStage['tags'].filter(t => t != null).map((tag, i) => {
                                          const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
                                          const bgColor = (typeof tag === 'object' && tag?.color) ? tag.color : '#e0e0e0';
                                          return tagName ? <span key={`${tagName}-${i}`} className='card-tag-chip' style={{ backgroundColor: bgColor }}>{tagName}</span> : null;
                                        })}
                                      </div>
                                      : <span className='quick-add-card__row-label'>Add label</span>
                                    }
                                  </div>
                                  <div className='quick-add-card__actions'>
                                    <button className='quick-add-card__save' onClick={handleQuickAddSave} disabled={quickAddStage['saving']}>
                                      {quickAddStage['saving'] ? <CircularProgress size={12} style={{ color: '#fff' }} /> : 'Save'}
                                    </button>
                                    <button className='quick-add-card__cancel' onClick={() => setQuickAddStage(null)} disabled={quickAddStage['saving']}>Cancel</button>
                                  </div>
                                </div>
                              )}
                              {stage['deals'].map((deal, index) => (
                                <Draggable key={deal['_id']} draggableId={deal['_id']} index={index}>
                                  {(provided, snapshot) => {
                                    // Merge our tilt transform with react-beautiful-dnd's positioning transform
                                    // Subtle tilt from top left corner when dragging
                                    const style = snapshot['isDragging'] && provided['draggableProps']['style']?.['transform']
                                      ? {
                                        ...provided['draggableProps']['style'],
                                        // Combine react-beautiful-dnd's transform with subtle rotation from top left
                                        // rotate(3deg) - subtle tilt, no scale to keep it natural
                                        transform: `${provided['draggableProps']['style']['transform']} rotate(3deg)`,
                                        transformOrigin: 'left top', // Tilt from top left corner
                                      } : provided['draggableProps']['style'];

                                    return (
                                      <div
                                        style={style}
                                        ref={provided['innerRef']}
                                        {...provided['draggableProps']}
                                        {...provided['dragHandleProps']}
                                        className={`box-card${snapshot['isDragging'] ? ' dragging' : ''}`}
                                      >
                                        <div className='title-box'>
                                          <span className='w-100' onClick={() => history.push(getWorkspacePath(`/deals/${deal['_id']}`, currentWorkspace?.['_id']))}>
                                            {editingDeal?.['id'] === deal['_id']
                                              ? <input ref={editInputRef} className='deal-rename-input' value={editingDeal['title']} onChange={(e) => setEditingDeal({ ...editingDeal, title: e.target.value })} onBlur={handleSaveRename} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRename(); if (e.key === 'Escape') setEditingDeal(null); }} onClick={(e) => e.stopPropagation()} />
                                              : <h3>{deal['title']}</h3>
                                            }
                                            <div className='name-owener-area'>
                                              <div className='left-name-area'>
                                                <div className='owner-name' style={{ position: 'relative' }}>
                                                  <span
                                                    style={{ cursor: 'pointer' }}
                                                    className={deal['ownerId']?.['firstName'] ? 'name-area' : 'name-area no-owner'}
                                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOwnerPickerDealId(ownerPickerDealId === deal['_id'] ? null : deal['_id']); }}
                                                  >
                                                    {deal['ownerId']?.['firstName']?.charAt(0) || <svg width="11" height="11" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 7.875C8.933 7.875 10.5 6.308 10.5 4.375C10.5 2.442 8.933 0.875 7 0.875C5.067 0.875 3.5 2.442 3.5 4.375C3.5 6.308 5.067 7.875 7 7.875Z" fill="#fff" /><path d="M12.025 13.125C12.025 10.7625 9.7738 8.8375 7.0003 8.8375C4.2268 8.8375 1.97559 10.7625 1.97559 13.125" fill="#fff" /></svg>}
                                                  </span>
                                                  {deal['ownerId']?.['firstName'] && <div className='overlaybox-tooltip'><p>{`${deal['ownerId']?.['firstName']} ${deal['ownerId']?.['lastName']}`}</p></div>}
                                                  {ownerPickerDealId === deal['_id'] && (
                                                    <div className='owner-picker-dropdown' onClick={(e) => e.stopPropagation()}>
                                                      <div className='owner-picker-header'>Assign Owner</div>
                                                      {members.map((m) => (
                                                        <div
                                                          key={m['_id']}
                                                          className={`owner-picker-item${deal['ownerId']?.['_id'] === m['_id'] ? ' active' : ''}`}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            dispatch(updateObject({
                                                              objectType: 'deals', id: deal['_id'], data: { ownerId: m['_id'] },
                                                              successCallback: () => {
                                                                setPipeline(prev => ({ ...prev, stages: prev['stages'].map(s => ({ ...s, deals: s['deals'].map(d => d['_id'] === deal['_id'] ? { ...d, ownerId: m } : d) })) }));
                                                                toast.success('Deal owner updated', { autoClose: 2000 });
                                                              }
                                                            }));
                                                            setOwnerPickerDealId(null);
                                                          }}
                                                        >
                                                          <span className='owner-picker-avatar'>{m['firstName']?.charAt(0) || m['email']?.charAt(0) || '?'}</span>
                                                          <span>{`${m['firstName'] || ''} ${m['lastName'] || ''}`.trim() || m['email']}</span>
                                                        </div>
                                                      ))}
                                                      {deal['ownerId'] && (
                                                        <div
                                                          className='owner-picker-item remove'
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            dispatch(updateObject({
                                                              objectType: 'deals', id: deal['_id'], data: { ownerId: null },
                                                              successCallback: () => {
                                                                setPipeline(prev => ({ ...prev, stages: prev['stages'].map(s => ({ ...s, deals: s['deals'].map(d => d['_id'] === deal['_id'] ? { ...d, ownerId: null } : d) })) }));
                                                                toast.success('Deal owner removed', { autoClose: 2000 });
                                                              }
                                                            }));
                                                            setOwnerPickerDealId(null);
                                                          }}
                                                        >
                                                          <span>No owner</span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className='card-tags-inline'>
                                                {deal['tags']?.filter(t => t != null).slice(0, 2).map((tag, i) => {
                                                  const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
                                                  const bgColor = (typeof tag === 'object' && tag?.color) ? tag.color : '#e0e0e0';
                                                  if (!tagName) return null;
                                                  return (
                                                    <span key={`${tagName}-${i}`} className='card-tag-chip' style={{ backgroundColor: bgColor }} onClick={(e) => { e.stopPropagation(); e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); setTagPickerPos({ top: r.bottom + 4, left: r.left }); setTagPickerDealId(tagPickerDealId === deal['_id'] ? null : deal['_id']); }}>
                                                      {tagName}
                                                    </span>
                                                  );
                                                })}
                                                {deal['tags']?.filter(t => t != null).length > 2 && (
                                                  <span className='card-tag-chip card-tag-more' onClick={(e) => { e.stopPropagation(); e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); setTagPickerPos({ top: r.bottom + 4, left: r.left }); setTagPickerDealId(tagPickerDealId === deal['_id'] ? null : deal['_id']); }}>
                                                    +{deal['tags'].filter(t => t != null).length - 2}
                                                  </span>
                                                )}
                                                <span className='card-tag-add-btn' onClick={(e) => { e.stopPropagation(); e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); setTagPickerPos({ top: r.bottom + 4, left: r.left }); setTagPickerDealId(tagPickerDealId === deal['_id'] ? null : deal['_id']); }}>
                                                  <svg width='12' height='12' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M1.33 2.67V6.78C1.33 7.14 1.47 7.49 1.72 7.74L7.59 13.61C8.11 14.13 8.96 14.13 9.48 13.61L13.61 9.48C14.13 8.96 14.13 8.11 13.61 7.59L7.74 1.72C7.49 1.47 7.14 1.33 6.78 1.33H2.67C1.93 1.33 1.33 1.93 1.33 2.67Z' stroke='#8C8C8C' strokeWidth='0.8' strokeLinecap='round' strokeLinejoin='round' /><path d='M4.67 5.67C5.22 5.67 5.67 5.22 5.67 4.67C5.67 4.12 5.22 3.67 4.67 3.67C4.12 3.67 3.67 4.12 3.67 4.67C3.67 5.22 4.12 5.67 4.67 5.67Z' stroke='#8C8C8C' strokeWidth='0.8' /></svg>
                                                </span>
                                                {tagPickerDealId === deal['_id'] && (
                                                  <div className='card-tag-picker-dropdown' style={{ position: 'fixed', top: tagPickerPos.top, left: tagPickerPos.left }} onClick={(e) => e.stopPropagation()}>
                                                    <TagsManager
                                                      mode="select"
                                                      skipRefresh={true}
                                                      objectType="deals"
                                                      selectedObjects={[deal['_id']]}
                                                      selectedTags={deal['tags'] || []}
                                                      onClose={() => setTagPickerDealId(null)}
                                                      onTagsApplied={(mergedTags) => {
                                                        setPipeline(prev => ({ ...prev, stages: prev['stages'].map(s => ({ ...s, deals: s['deals'].map(d => d['_id'] === deal['_id'] ? { ...d, tags: mergedTags } : d) })) }));
                                                      }}
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                              <div className='right-price-area'>
                                                <div className='owner-name'>
                                                  {deal['amount'] > 0 && <p>{deal['amount'].toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>}
                                                </div>
                                              </div>
                                            </div>
                                          </span>
                                          <div className={`checkbox-right-area ${selectItems.length > 0 && ' show-checkbox'}`}>
                                            <Checkbox checked={selectItems.includes(deal['_id'])} onChange={() => handleSelectCheckBox(deal['_id'])} />
                                            <button className='deal-action-btn' onClick={(e) => handleStartRename(e, deal)}>
                                              <svg width='14' height='14' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M6.87 2.01L2.505 6.645C2.34 6.82 2.18 7.165 2.15 7.4L1.955 9.085C1.89 9.665 2.305 10.06 2.88 9.96L4.555 9.67C4.79 9.63 5.125 9.455 5.29 9.275L9.655 4.64C10.41 3.845 10.75 2.94 9.575 1.83C8.405 0.73 7.625 1.215 6.87 2.01Z' stroke='#8C8C8C' strokeWidth='0.8' strokeMiterlimit='10' strokeLinecap='round' strokeLinejoin='round' /><path d='M6.1875 2.7325C6.4525 4.2075 7.6375 5.3225 9.1225 5.5075' stroke='#8C8C8C' strokeWidth='0.8' strokeMiterlimit='10' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                            </button>
                                            <button className='deal-action-btn' onClick={(e) => { e.stopPropagation(); setIsSideViewModal(deal); }}>
                                              <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M10.3866 8.00001C10.3866 9.32001 9.31995 10.3867 7.99995 10.3867C6.67995 10.3867 5.61328 9.32001 5.61328 8.00001C5.61328 6.68001 6.67995 5.61334 7.99995 5.61334C9.31995 5.61334 10.3866 6.68001 10.3866 8.00001Z' stroke='#8C8C8C' strokeWidth='0.8' strokeLinecap='round' strokeLinejoin='round' /><path d='M7.99999 13.5133C10.3533 13.5133 12.5467 12.1267 14.0733 9.72668C14.6733 8.78668 14.6733 7.20668 14.0733 6.26668C12.5467 3.86668 10.3533 2.48001 7.99999 2.48001C5.64666 2.48001 3.45332 3.86668 1.92666 6.26668C1.32666 7.20668 1.32666 8.78668 1.92666 9.72668C3.45332 12.1267 5.64666 13.5133 7.99999 13.5133Z' stroke='#8C8C8C' strokeWidth='0.8' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                            </button>
                                            <button className='deal-action-btn' onClick={(e) => handleCopyLink(e, deal['_id'])}>
                                              <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M6.667 8.667C6.953 9.049 7.319 9.366 7.738 9.595C8.157 9.824 8.621 9.961 9.098 9.995C9.575 10.029 10.054 9.96 10.502 9.793C10.951 9.626 11.359 9.365 11.7 9.027L13.7 7.027C14.317 6.381 14.658 5.519 14.648 4.627C14.638 3.734 14.278 2.88 13.647 2.249C13.016 1.618 12.162 1.258 11.269 1.248C10.377 1.238 9.515 1.579 8.869 2.196L7.776 3.283' stroke='#8C8C8C' strokeWidth='0.8' strokeLinecap='round' strokeLinejoin='round' /><path d='M9.333 7.333C9.047 6.951 8.682 6.634 8.263 6.405C7.843 6.176 7.38 6.039 6.903 6.005C6.426 5.971 5.947 6.04 5.498 6.207C5.049 6.374 4.641 6.635 4.3 6.973L2.3 8.973C1.683 9.619 1.342 10.481 1.352 11.373C1.362 12.266 1.722 13.12 2.353 13.751C2.984 14.382 3.838 14.742 4.731 14.752C5.624 14.762 6.486 14.421 7.131 13.804L8.218 12.717' stroke='#8C8C8C' strokeWidth='0.8' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                            </button>
                                          </div>
                                        </div>
                                        {(deal['activityInfo']?.['subText'] || deal['contacts']?.length > 0) && (
                                          <span className='w-100' onClick={() => history.push(getWorkspacePath(`/deals/${deal['_id']}`, currentWorkspace?.['_id']))}>
                                            <div className='detial-box'>
                                              <p>{deal['activityInfo']?.['subText']}</p>
                                              <div className='user-name-area'>
                                                {deal['contacts'] && deal['contacts'].slice(0, 3).map((contact) => (
                                                  <span key={contact['_id'] || contact['firstName']} className='name-area'>
                                                    {contact?.['firstName']?.charAt(0) || ''}
                                                    <div className='overlaybox-tooltip'>
                                                      <p>{`${contact?.['firstName']} ${contact?.['lastName']}`}</p>
                                                    </div>
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          </span>
                                        )}
                                        <div className='links-area'>
                                          <ul>
                                            <li>
                                              <button onClick={() => { dispatch(setActiveActivityBox({ activityBox: 'note', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false); }}>
                                                <i className='icon'>
                                                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M8.6 14V13.4C8.6 11.1373 8.6 10.0059 9.30296 9.30294C10.0059 8.59998 11.1373 8.59998 13.4 8.59998H14M6.8 2H9.2C11.4627 2 12.5941 2 13.297 2.70294C14 3.40589 14 4.53726 14 6.79998V7.04448C14 8.10576 14 8.6364 13.7894 9.10542C13.5787 9.57444 13.1821 9.927 12.3889 10.6321L9.95888 12.7921C9.28964 13.387 8.95502 13.6844 8.5466 13.841L8.5343 13.8457C8.12498 14 7.67726 14 6.78192 14C4.53677 14 3.41419 14 2.71302 13.3071L2.69293 13.287C2 12.5858 2 11.4632 2 9.2181V6.79998C2 4.53726 2 3.40589 2.70294 2.70294C3.40589 2 4.53726 2 6.8 2Z' stroke='#212121' stroke-linejoin='round' /></svg>
                                                  <div className='overlaybox-tooltip'>
                                                    <p>Add Note</p>
                                                  </div>
                                                </i>
                                              </button>
                                            </li>
                                            <li>
                                              <button onClick={() => { dispatch(setActiveActivityBox({ activityBox: 'task', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false); }}>
                                                <i className='icon'>
                                                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M7.33301 13H13.9997' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M7.33301 8.33325H13.9997' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M7.33301 3.6665H13.9997' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M2 3.66659L2.66667 4.33325L4.66667 2.33325' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M2 8.33333L2.66667 9L4.66667 7' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M2 12.9998L2.66667 13.6665L4.66667 11.6665' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /></svg>
                                                  <div className='overlaybox-tooltip'>
                                                    <p>Add Task</p>
                                                  </div>
                                                </i>
                                              </button>
                                            </li>
                                            {/* Call - only show if deal has contacts with phone */}
                                            {deal['contacts']?.some(c => c.phone) && (
                                              <li>
                                                <button onClick={() => { dispatch(setActiveActivityBox({ activityBox: 'call', associations: { deals: [deal['_id']], contacts: deal['contacts']?.filter(c => c.phone).map(c => c._id) || [] }, source: 'pipeline' })); setIsSideViewModal(false); }}>
                                                  <i className='icon'>
                                                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M13 10.8127C13 11.029 12.9519 11.2514 12.8498 11.4677C12.7476 11.684 12.6154 11.8883 12.4412 12.0806C12.1467 12.4051 11.8222 12.6395 11.4557 12.7897C11.0951 12.9399 10.7046 13.018 10.2839 13.018C9.67101 13.018 9.01602 12.8738 8.32499 12.5794C7.63395 12.2849 6.94291 11.8883 6.25789 11.3896C5.56685 10.8848 4.91187 10.326 4.28693 9.70706C3.668 9.08212 3.10916 8.42714 2.61042 7.74211C2.11768 7.05709 1.72108 6.37206 1.43265 5.69304C1.14422 5.00801 1 4.35303 1 3.72809C1 3.31948 1.07211 2.92889 1.21632 2.56835C1.36054 2.2018 1.58888 1.8653 1.90736 1.56485C2.29194 1.18628 2.71257 1 3.15724 1C3.32549 1 3.49374 1.03605 3.64397 1.10816C3.8002 1.18027 3.93841 1.28843 4.04657 1.44467L5.44066 3.40961C5.54882 3.55984 5.62694 3.69805 5.68102 3.83025C5.7351 3.95643 5.76515 4.08262 5.76515 4.1968C5.76515 4.34101 5.72308 4.48523 5.63896 4.62343C5.56084 4.76164 5.44667 4.90586 5.30245 5.05007L4.84577 5.52479C4.77967 5.59089 4.74962 5.669 4.74962 5.76515C4.74962 5.81322 4.75563 5.85528 4.76765 5.90335C4.78568 5.95143 4.80371 5.98748 4.81572 6.02353C4.92389 6.22183 5.11017 6.48022 5.37456 6.79269C5.64497 7.10516 5.9334 7.42364 6.24587 7.74211C6.57036 8.06059 6.88282 8.35503 7.2013 8.62544C7.51377 8.88983 7.77216 9.0701 7.97646 9.17827C8.00651 9.19028 8.04256 9.20831 8.08463 9.22634C8.1327 9.24437 8.18077 9.25037 8.23485 9.25037C8.33701 9.25037 8.41512 9.21432 8.48122 9.14822L8.93791 8.69755C9.08813 8.54732 9.23235 8.43315 9.37056 8.36104C9.50876 8.27691 9.64697 8.23485 9.7972 8.23485C9.91137 8.23485 10.0315 8.25889 10.1637 8.31297C10.2959 8.36705 10.4342 8.44517 10.5844 8.54732L12.5734 9.95944C12.7296 10.0676 12.8378 10.1938 12.9039 10.344C12.9639 10.4942 13 10.6445 13 10.8127Z' stroke='#212121' stroke-miterlimit='10' /></svg>
                                                    <div className='overlaybox-tooltip'>
                                                      <p>Call</p>
                                                    </div>
                                                  </i>
                                                </button>
                                              </li>
                                            )}
                                            {/* SMS - only show if deal has contacts with phone */}
                                            {deal['contacts']?.some(c => c.phone) && (
                                              <li>
                                                <button onClick={() => { dispatch(setActiveActivityBox({ activityBox: 'sms', associations: { deals: [deal['_id']], contacts: deal['contacts']?.filter(c => c.phone).map(c => c._id) || [] }, objectData: deal, source: 'pipeline' })); setIsSideViewModal(false); }}>
                                                  <i className='icon'>
                                                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z' stroke='#212121' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                                    <div className='overlaybox-tooltip'>
                                                      <p>Send SMS</p>
                                                    </div>
                                                  </i>
                                                </button>
                                              </li>
                                            )}
                                            <li>
                                              <button onClick={() => { dispatch(setActiveActivityBox({ activityBox: 'mail', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false); }}>
                                                <i className='icon'>
                                                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M11.9997 2.66675H3.99967C2.52691 2.66675 1.33301 3.86065 1.33301 5.33341V10.6667C1.33301 12.1395 2.52691 13.3334 3.99967 13.3334H11.9997C13.4724 13.3334 14.6663 12.1395 14.6663 10.6667V5.33341C14.6663 3.86065 13.4724 2.66675 11.9997 2.66675Z' stroke='#212121' /><path d='M3.33301 6L5.39651 7.76871C6.63872 8.83344 7.25981 9.36584 7.99967 9.36584C8.73954 9.36584 9.36061 8.83344 10.6028 7.76871L12.6663 6' stroke='#212121' stroke-linejoin='round' /><path d='M5.33301 8L3.33301 10' stroke='#212121' stroke-linejoin='round' /><path d='M10.666 8L12.666 10' stroke='#212121' stroke-linejoin='round' /></svg>
                                                  <div className='overlaybox-tooltip'>
                                                    <p>Send Email</p>
                                                  </div>
                                                </i>
                                              </button>
                                            </li>
                                            <li>
                                              <button onClick={() => { dispatch(setActiveActivityBox({ activityBox: 'schedule', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false); }}>
                                                <i className='icon'>
                                                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5.33301 1.33325V3.33325' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M10.667 1.33325V3.33325' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M2.33301 6.05981H13.6663' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M14 5.66659V11.3333C14 13.3333 13 14.6666 10.6667 14.6666H5.33333C3 14.6666 2 13.3333 2 11.3333V5.66659C2 3.66659 3 2.33325 5.33333 2.33325H10.6667C13 2.33325 14 3.66659 14 5.66659Z' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M7.99707 9.1333H8.00307' stroke='#212121' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /><path d='M5.5293 9.1333H5.53528' stroke='#212121' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /><path d='M5.5293 11.1333H5.53528' stroke='#212121' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /></svg>
                                                  <div className='overlaybox-tooltip'>
                                                    <p>Add Meeting</p>
                                                  </div>
                                                </i>
                                              </button>
                                            </li>
                                          </ul>
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              <button className='stage-add-deal-link' onClick={() => openQuickAdd(stage['stageId'])}><svg width='9' height='9' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M6 1V11M1 6H11' stroke='#9ca3af' strokeWidth='1.5' strokeLinecap='round' /></svg>Quick Add</button>
                            </div>
                            <div className='bottom-area'>
                              <h4>
                                <span>Total:</span> {
                                  stage['deals'].reduce((acc, deal) => {
                                    let num = 0;
                                    if (deal['amount']) num = parseFloat(deal['amount'] || 0);
                                    return acc + num;
                                  }, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
                                }
                              </h4>
                            </div>
                          </div>
                        </div>
                      }
                    </section>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          )) : null}
        </div>
      </DragDropContext>

      {/* Quick-add owner picker portal */}
      {quickAddStage?.['showOwnerPicker'] && (
        <div className='quick-add-portal-dropdown' style={{ top: quickAddOwnerPos.top, left: quickAddOwnerPos.left }} onClick={(e) => e.stopPropagation()}>
          <div className='owner-picker-header'>Assign Owner</div>
          {members.map((m) => (
            <div key={m['_id']} className={`owner-picker-item${quickAddStage['owner']?.['_id'] === m['_id'] ? ' active' : ''}`} onClick={(e) => { e.stopPropagation(); setQuickAddStage(prev => ({ ...prev, owner: m, showOwnerPicker: false })); }}>
              <span className='owner-picker-avatar'>{m['firstName']?.charAt(0) || m['email']?.charAt(0) || '?'}</span>
              <span>{`${m['firstName'] || ''} ${m['lastName'] || ''}`.trim() || m['email']}</span>
            </div>
          ))}
          {quickAddStage['owner'] && (
            <div className='owner-picker-item remove' onClick={(e) => { e.stopPropagation(); setQuickAddStage(prev => ({ ...prev, owner: null, showOwnerPicker: false })); }}>
              <span>No owner</span>
            </div>
          )}
        </div>
      )}

      {/* Quick-add tag picker portal */}
      {quickAddStage?.['showTagPicker'] && (
        <div className='quick-add-portal-dropdown' style={{ top: tagPickerPos.top, left: tagPickerPos.left }} onClick={(e) => e.stopPropagation()}>
          <TagsManager
            mode="form"
            skipRefresh={true}
            objectType="deals"
            selectedTags={quickAddStage['tags'] || []}
            onClose={() => setQuickAddStage(prev => ({ ...prev, showTagPicker: false }))}
            onTagsChange={(tags) => { setQuickAddStage(prev => ({ ...prev, tags })); }}
          />
        </div>
      )}
    </>
  )
}

export default PipelineView;